import { useState } from "react";
import { Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";

interface SuperAdminDeleteMatchButtonProps {
	matchId: string;
	redirectTo?: string;
}

export const SuperAdminDeleteMatchButton = ({ matchId, redirectTo = "/matches" }: SuperAdminDeleteMatchButtonProps) => {
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	if (!isSuperAdmin || !matchId)
		return null;

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			const { error } = await supabase.from("matches").delete().eq("id", matchId);
			if (error) throw error;
			toast({ title: t("common.success"), description: t("superAdmin.deleteMatch.success") });
			navigate(redirectTo);
		} catch {
			toast({ title: t("common.error"), description: t("superAdmin.deleteMatch.error"), variant: "destructive" });
		} finally {
			setIsDeleting(false);
			setOpen(false);
		}
	};

	return (
		<>
			<Button
				type="button"
				variant="outline"
				size="icon"
				className="flex items-center justify-center text-red-600 hover:text-red-700"
				onClick={() => setOpen(true)}
				disabled={isDeleting}
				title={t("superAdmin.deleteMatch.button")}
			>
				<Trash className="h-4 w-4" />
			</Button>
			<AlertDialog open={open} onOpenChange={setOpen}>
				<AlertDialogContent className="sm:max-w-[425px]">
					<AlertDialogHeader>
						<AlertDialogTitle>{t("superAdmin.deleteMatch.title")}</AlertDialogTitle>
						<AlertDialogDescription>{t("superAdmin.deleteMatch.description")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-red-600 hover:bg-red-700 text-white"
							disabled={isDeleting}
						>
							{t("common.delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
